
// First Created: 06 Jan 2026
// Author: Atul Pandey

/**
 * normalizeCollectiveActions
 *
 * Purpose:
 * Build user-centric collective groupings from a flat list of actions.
 * Output is safe for performance aggregation and UI rendering.
 *
 * Key Principles:
 * 1. Anchor action MUST belong to logged-in user
 * 2. Collective is defined by shared sponsor action
 * 3. Sponsor vs Assignee is a ROLE, not a grouping key
 * 4. Backend does NOT categorize — frontend decides
 *
 * @param {Array} actions - All fetched actions (self + sponsor + assignee + collective)
 * @param {String} loggedInUserId
 */

export function normalizeCollectiveActions(actions = [], loggedInUserId) {
    
  const actionById = new Map();
  actions.forEach(a => actionById.set(String(a._id), a));

  const collectiveGroups = [];
  const selfActions = [];

  // ---------------------------------------------------
  // Helper: Is this action owned by logged-in user?
  // ---------------------------------------------------
  const isOwnedByUser = action =>
    String(action.userId?._id) === String(loggedInUserId);

  // ---------------------------------------------------
  // Helper: Is this action a sponsor action?
  // (must have assignedUsers)
  // ---------------------------------------------------
  const isSponsorAction = action =>
    !action.assignedFromAction &&
    Array.isArray(action.assignedUsers) &&
    action.assignedUsers.length > 0;

  // ---------------------------------------------------
  // Helper: Resolve sponsor action [SAFE]
  // ---------------------------------------------------
  function getSponsorAction(action) {
    if (isSponsorAction(action)) return action;

    if (action.assignedFromAction) {
      return actionById.get(String(action.assignedFromAction)) || null;
    }

    return null;
  }

  // ---------------------------------------------------
  // Helper: Determine user role for this collective
  // ---------------------------------------------------
  function getUserRole(anchorAction) {
    return isSponsorAction(anchorAction) ? "Sponsor" : "Assignee";
  }

  // ---------------------------------------------------
  // Track anchors already processed (by anchorActionId)
  // ---------------------------------------------------
  const processedAnchors = new Set();

  // ---------------------------------------------------
  // MAIN LOOP — user-centric
  // ---------------------------------------------------
  actions.forEach(action => {
    if (!isOwnedByUser(action)) return;

    // -----------------------------------------------
    // CASE 1 — Pure Self Action
    // -----------------------------------------------
    if (
      !action.assignedFromAction &&
      (!action.assignedUsers || action.assignedUsers.length === 0)
    ) {
      selfActions.push(action);
      return;
    }

    // -----------------------------------------------
    // CASE 2 — Collective Action (Sponsor or Assignee)
    // -----------------------------------------------
    let anchorAction = null;
    let sponsorAction = null;

    // User is Sponsor
    if (isSponsorAction(action)) {
      anchorAction = action;
      sponsorAction = action;
    }

    // User is Assignee
    if (action.assignedFromAction) {
      anchorAction = action;
      sponsorAction = getSponsorAction(action);
    }

    if (!anchorAction || !sponsorAction) return;

    const anchorId = String(anchorAction._id);
    if (processedAnchors.has(anchorId)) return;
    processedAnchors.add(anchorId);

    // -----------------------------------------------
    // Build collective set:
    // Sponsor + all assignee clones
    // -----------------------------------------------
    const collectiveActions = [];

    // Include sponsor
    collectiveActions.push(sponsorAction);

    // Include all assignees under sponsor
    actions.forEach(a => {
      if (
        a.assignedFromAction &&
        String(a.assignedFromAction) === String(sponsorAction._id)
      ) {
        collectiveActions.push(a);
      }
    });

    // -----------------------------------------------
    // Push normalized group
    // -----------------------------------------------
    collectiveGroups.push({
      anchorActionId: anchorId,          // MUST belong to logged-in user
      anchorAction,
      role: getUserRole(anchorAction),   // Sponsor | Assignee
      collectiveActions,
    });
  });

  return {
    collectiveGroups,
    selfActions,
  };
}